import BoqItemQuantityChip from "./BoqItemQuantityChip";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { useCallback, useEffect, useRef, useState } from "react";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { getDisplayQuantity, getEstimateNumberValueBasedOnSettings, getMeasurementsList } from "@Actions/estimation";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "@i18n";
import Text from "@CubeComponents/html/Text/Text";
import type { RootState } from "src/reducers";
import type { EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";
import type { TakeoffMeasurement, MeasurementUnit } from "@Helpers/types/estimation/takeoffTypes";
import type { GetTakeoffsListAttachedToItemResponse } from "@ServerTypes/takeoffs/takeoffs.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type BoqItemDetails = {
  manualQuantity?: number | string;
  quantityFromTakeoffs?: number | string;
  hasChild?: boolean;
};

type MeasurementRow = TakeoffMeasurement & {
  unit: MeasurementUnit & { type: { name: string } };
  quantity: number;
  multiplier: number;
  id?: string;
};

/**
 * A row of the attached-takeoffs grid. Derived from `getTakeoffsListAttachedToItemResponse` because
 * that endpoint returns a documented SUBSET of a takeoff (`takeoffListItemAttached`: no `createdBy`,
 * `quantity` or `type`) — the previous `TakeoffListItem &` base demanded fields the API never sends.
 * Everything this component reads (takeOffItemId, additionalQuantity, measurements, measurementUnit)
 * is present in that subset.
 */
export type TakeoffSelectionRow = NonNullable<GetTakeoffsListAttachedToItemResponse["result"]>[number];

type SlickGridHandle = {
  slickGrid: {
    getSelectedRows: () => number[];
    getDataItem: (row: number) => TakeoffSelectionRow;
  };
};

type BoqItemQuantityDetailsProps = {
  itemCurrentDetails: BoqItemDetails;
  updateItemDetails: (
    args: {
      value: number | null;
      previousValue?: number | null;
      event?: Event;
      target?: { value: string; name?: string };
    },
    field?: string,
    checkNumberIsPositive?: boolean,
  ) => void;
  isActionPermission: boolean;
  attachedTakeoffsList: TakeoffSelectionRow[];
};
const BoqItemQuantityDetails = ({
  itemCurrentDetails,
  updateItemDetails,
  isActionPermission,
  attachedTakeoffsList,
}: BoqItemQuantityDetailsProps) => {
  const { t } = useTranslation();
  // Route params are the external entry point for these ids; the backend route schema validates them.
  const params = useParams<{ estimateId: ObjectIdString; versionId: ObjectIdString }>();
  const takeoffDataGrid = useRef<SlickGridHandle | null>(null);
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;
  const [currentSelectedTakeoff, setCurrentSelectedTakeoff] = useState<ObjectIdString | null>(null);
  const [measurementsDataset, setMeasurementsDataset] = useState<MeasurementRow[]>([]);
  const [showGridLoader, setShowGridLoader] = useState(false);

  const fetchMeasurements = useCallback(async () => {
    setShowGridLoader(true);
    const data = {
      params: {
        estimateId: params.estimateId!,
        takeOffItemId: currentSelectedTakeoff
          ? [currentSelectedTakeoff]
          : attachedTakeoffsList.map((takeoff: TakeoffSelectionRow) => takeoff.takeOffItemId),
        versionId: params.versionId,
        takeOffIncludedMeasuremnt: true,
      },
    };
    const response = await getMeasurementsList({ value: data });
    const measurementsWithTotal = [
      ...(response.data.result ?? []),
      // {
      //   name: "Total",
      //   id: "total_row",
      //   quantity: currentSelectedTakeoff
      //     ? getTakeoffQuantity(attachedTakeoffsList.find((takeoff: any) => takeoff.takeOffItemId == currentSelectedTakeoff))
      //     : getEstimateNumberValueBasedOnSettings(itemCurrentDetails.quantityFromTakeoffs, estimateData,"measurement"),
      // },
    ];
    setMeasurementsDataset(measurementsWithTotal as MeasurementRow[]);
    setShowGridLoader(false);
  }, [params.estimateId, params.versionId, currentSelectedTakeoff, attachedTakeoffsList]);

  useEffect(() => {
    if (attachedTakeoffsList.length) {
      fetchMeasurements();
    }
  }, [attachedTakeoffsList, fetchMeasurements]);

  const takeoffSelectionEvent = () => {
    const selectedTakeoff =
      (takeoffDataGrid.current?.slickGrid
        .getSelectedRows()
        .map((data: number) => takeoffDataGrid.current?.slickGrid.getDataItem(data))
        .filter(Boolean) as TakeoffSelectionRow[] | undefined) ?? [];
    if (selectedTakeoff.length == 1 && selectedTakeoff[0]) {
      setCurrentSelectedTakeoff(selectedTakeoff[0].takeOffItemId);
    } else {
      setCurrentSelectedTakeoff(null);
    }
  };

  const getTakeoffQuantity = (params: TakeoffSelectionRow) => {
    let totalQuantity = parseFloat(String(params.additionalQuantity ?? 0));
    if (params.measurements?.length) {
      params.measurements.map((measurement: TakeoffMeasurement) => {
        if (measurement.quantity && !isNaN(measurement.quantity)) {
          totalQuantity += Number(measurement.quantity) * measurement.multiplier;
        }
      });
    }
    const result = getDisplayQuantity({
      factor: params.measurementUnit.factor,
      quantity: totalQuantity,
    }).quantity;

    return getEstimateNumberValueBasedOnSettings(Number(result), estimateData, "takeoff");
  };

  const quantityTemplate = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: TakeoffSelectionRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    const result = getTakeoffQuantity(params);

    root.render(<div>{result}</div>);
    return container;
  };

  const measurementTemplate = (_data: unknown, _v: unknown, _a: unknown, _b: unknown, params: MeasurementRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    if (params.id === "total_row") {
      root.render(params.quantity);
    } else {
      const value = getDisplayQuantity({
        quantity: params.quantity * params.multiplier,
        factor: params.unit.factor,
      }).quantity;

      root.render(getEstimateNumberValueBasedOnSettings(value ?? 0, estimateData, "measurement"));
    }
    return container;
  };

  const unitTemplate = (_data: unknown, _v: unknown, _a: unknown, _b: unknown, params: MeasurementRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    if (params.id === "total_row") {
      root.render(<div></div>);
    } else {
      root.render(params.unit.type.name);
    }
    return container;
  };

  const takeoffGridColumns = useRef([
    {
      field: "name",
      id: "name",
      name: t("Takeoff Name"),
      textAlign: "Left",
      wrapText: true,
    },
    {
      field: "quantity",
      id: "quantity",
      name: t("Quantity"),
      textAlign: "Left",
      formatter: quantityTemplate,
    },
  ]);

  const measurementGridColumns = useRef([
    {
      field: "name",
      id: "name",
      name: t("Measurement Name"),
      textAlign: "Left",
      wrapText: true,
      minWidth: 150,
    },
    {
      field: "type",
      id: "type",
      name: t("Type"),
      textAlign: "Left",
      formatter: unitTemplate,
      minWidth: 50,
    },
    {
      field: "nature",
      id: "nature",
      name: t("Nature"),
      textAlign: "Left",
      minWidth: 80,
    },
    {
      field: "fileName",
      id: "fileName",
      name: t("File"),
      textAlign: "Left",
      wrapText: true,
      minWidth: 150,
    },
    {
      field: "quantity",
      id: "quantity",
      name: t("Value"),
      textAlign: "Left",
      formatter: measurementTemplate,
      minWidth: 100,
    },
  ]);

  const attachedTakeoffs = () => {
    return (
      <div className="flex flex-1 overflow-auto h-full">
        <SlickGrid1
          ref={takeoffDataGrid as any}
          selectCheckBoxOnRowClick
          columnDefinitions={takeoffGridColumns.current as any}
          rowHeight={40 as any}
          onSelectedRowIdsChanged={takeoffSelectionEvent}
          dataset={attachedTakeoffsList}
          showPageCount={false}
          disableMenuOptions
        />
      </div>
    );
  };

  const selectedTakeoffMeasurements = () => {
    return (
      <div className="flex flex-2 overflow-auto h-full">
        <SlickGrid1
          columnDefinitions={measurementGridColumns.current as any}
          rowHeight={40 as any}
          showLoader={showGridLoader}
          dataset={measurementsDataset}
          showPageCount={false}
          disableMenuOptions
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6 pb-4 h-full w-full">
      <div className="flex flex-col gap-6 py-6">
        <div className="flex items-center">
          <Text className="text-base font-semibold flex flex-1">{t("Quantity")}</Text>
          <div className="w-full flex flex-[5] gap-6 justify-between">
            <BoqItemQuantityChip
              manualQuantity={itemCurrentDetails.manualQuantity}
              quantityFromTakeoffs={itemCurrentDetails.quantityFromTakeoffs}
            />
          </div>
        </div>
        <div className="flex gap-5">
          <div className="flex flex-1 items-center">
            <Text className="text-base font-semibold flex flex-1">{t("Manual Quantity")}</Text>
            <div className="w-full flex flex-2 gap-6 justify-between">
              <NumericTextBoxComponent
                placeholder="Enter Manual Quantity"
                value={itemCurrentDetails.manualQuantity ? Number(itemCurrentDetails.manualQuantity) : undefined}
                onChange={(args) => updateItemDetails(args, "manualQuantity")}
                min={0}
                showSpinButton={false}
                disabled={!isActionPermission || itemCurrentDetails.hasChild}
              />
            </div>
          </div>
          <div className="flex flex-1 items-center">
            <Text className="text-base font-semibold flex flex-1">{t("Takeoff Quantity")}</Text>
            <div className="w-full flex flex-2 gap-6 justify-between">
              <TextFieldComponent
                className="w-full"
                disabled
                placeholder=""
                value={getEstimateNumberValueBasedOnSettings(
                  Number(itemCurrentDetails.quantityFromTakeoffs ?? 0),
                  estimateData,
                  "takeoff",
                )}
              />
            </div>
          </div>
        </div>
      </div>
      {attachedTakeoffsList.length > 0 && (
        <div className="flex flex-col h-full gap-6">
          <div>
            <Text className="text-xl font-semibold">{t("Linked Measurements")}</Text>
          </div>
          <div className="flex gap-4 h-full">
            {attachedTakeoffs()}
            {selectedTakeoffMeasurements()}
          </div>
        </div>
      )}
    </div>
  );
};

export default BoqItemQuantityDetails;
